// Decides whether an access token request can be answered from the shared Auth0 cache without the
// portal's token lock (auth/tokenRequestLock), which only refresh-token exchanges need (AB#5012).
//
// Asking auth0-spa-js for a cached token with `cacheMode: 'cache-only'` never exchanges a refresh
// token, but it is not read-only: an entry within the SDK's 60-second expiry leeway is rewritten
// without its access token (keeping the refresh token this tab read), or removed, and a reached
// session ceiling clears the local session. Done outside the lock, that rewrite could undo an entry
// another tab has just renewed: the refresh-token store (auth/refreshTokenStore) ignores the stale
// refresh token it would put back, but the renewed access token would be lost. So the SDK is asked
// only when this read-only check of the localStorage cache finds nothing it would rewrite: every entry
// it could resolve for the request still holds an access token, for the displayed organisation and the
// requested API, fresh well beyond its leeway.
//
// This mirrors auth0-spa-js 2.24 (cache/shared.ts, cache/cache-manager.ts). Entries live under
// `@@auth0spajs@@::<clientId>::<audience>::<scope>` as `{ body: { access_token, ... }, expiresAt }`,
// with `expiresAt` in seconds, and the ID token under `@@auth0spajs@@::<clientId>::@@user@@`. The SDK
// resolves the exact key, or else the first key with the same client and audience whose scope covers
// its request (holds every scope asked for). It asks for the requested scopes plus its own defaults,
// so whichever entry it resolves covers the requested scopes, and every such entry is checked. An
// entry left under a scope that no longer covers the request, after a scope change, is never resolved
// or refreshed, so it is ignored rather than sending every request to the lock. The organisation is
// not part of the key: a cached token can belong to another organisation.
import { isAccessTokenForAudience, isAccessTokenForOrganization } from './accessTokenOrganization';
import { buildIdTokenCacheKey } from './crossTabAccount';

const AUTH0_CACHE_KEY_PREFIX = '@@auth0spajs@@';

// Twice the SDK's 60-second leeway, so the SDK still finds the entry fresh when it reads it.
export const CACHED_ACCESS_TOKEN_FRESHNESS_MARGIN_SECONDS = 120;

export interface CachedAccessTokenRequest {
  clientId: string;
  audience?: string;
  // The scopes the request asks for, space-separated, as passed to getAccessTokenSilently.
  scope?: string;
  // The organisation of the account on screen; undefined for an organisation-less account.
  organization?: string;
  now?: number;
}

type JsonObject = Record<string, unknown>;

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readEntry = (storage: Storage, key: string): JsonObject | undefined => {
  const json = storage.getItem(key);
  if (!json) return undefined;

  try {
    const entry: unknown = JSON.parse(json);
    return isJsonObject(entry) ? entry : undefined;
  } catch {
    return undefined;
  }
};

const isFreshUntil = (expiresAtSeconds: unknown, nowSeconds: number) =>
  typeof expiresAtSeconds === 'number' && expiresAtSeconds - CACHED_ACCESS_TOKEN_FRESHNESS_MARGIN_SECONDS >= nowSeconds;

// The SDK reads the ID token after the access token and, without one, looks it up through the token
// entries, which can rewrite them. Its session ceiling (`session_expiry`), once reached, clears the
// whole local session.
const hasUsableIdToken = (storage: Storage, clientId: string, nowSeconds: number) => {
  const entry = readEntry(storage, buildIdTokenCacheKey(clientId));
  if (!entry) return false;

  const claims = isJsonObject(entry.decodedToken) ? entry.decodedToken.claims : undefined;
  const sessionExpiry = isJsonObject(claims) ? claims.session_expiry : undefined;
  return sessionExpiry === undefined || isFreshUntil(sessionExpiry, nowSeconds);
};

// The SDK's covering rule (CacheManager.matchExistingCacheKey): the entry has a scope holding every
// requested scope. With no requested scope, every entry is checked.
const coversScopes = (entryScope: string | undefined, requestedScopes: string[]) => {
  if (!requestedScopes.length) return true;
  if (!entryScope) return false;

  const entryScopes = new Set(entryScope.split(' '));
  return requestedScopes.every((requestedScope) => entryScopes.has(requestedScope));
};

const isTokenEntryKeyFor = (key: string, clientId: string, audience: string, requestedScopes: string[]) => {
  const [prefix, keyClientId, keyAudience, keyScope] = key.split('::');
  return prefix === AUTH0_CACHE_KEY_PREFIX
    && keyClientId === clientId
    && keyAudience === audience
    && coversScopes(keyScope, requestedScopes);
};

/**
 * True only when auth0-spa-js can answer a `cache-only` request for this audience and scope from the
 * shared localStorage cache without writing to it, with a token for the given organisation and API.
 * Reads only; anything unexpected answers false, so the request takes the token lock as before.
 */
export const hasFreshCachedAccessToken = ({ clientId, audience, scope, organization, now = Date.now() }: CachedAccessTokenRequest): boolean => {
  if (!clientId || !audience) return false;

  try {
    const storage = window.localStorage;
    const nowSeconds = Math.floor(now / 1_000);
    if (!hasUsableIdToken(storage, clientId, nowSeconds)) return false;

    const requestedScopes = (scope ?? '').split(/\s+/).filter(Boolean);
    const keys = Object.keys(storage).filter((key) => isTokenEntryKeyFor(key, clientId, audience, requestedScopes));
    return keys.length > 0 && keys.every((key) => {
      const entry = readEntry(storage, key);
      const body = isJsonObject(entry?.body) ? entry.body : undefined;
      const accessToken = body?.access_token;

      return isFreshUntil(entry?.expiresAt, nowSeconds)
        && typeof accessToken === 'string'
        && isAccessTokenForOrganization(accessToken, organization)
        && isAccessTokenForAudience(accessToken, audience);
    });
  } catch {
    return false;
  }
};
